import { Spinner } from 'common/spinner.js';
import { Glossary } from 'components/glossary/glossary.js';
import { ModuleLoad } from 'layout/module-load/module-load.js';
import * as React from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux-state/configure-store.js';
import { IModuleConfig } from 'redux-state/layout/types.modules.js';
import { REDUX_SEARCH_CLOSE_OVERLAY, REDUX_SEARCH_SAVE_RECENT_SEARCH } from 'redux-state/search/reducer.js';
import { ISearchResult, SEARCH_THRESHOLD, SearchStatus } from 'redux-state/search/types.js';
import { ModulePath } from 'utilities/modulePath/modulePath.js';
import { SearchContentResults } from './search-content-results.js';

interface IProps {
  text: string;
}
const _SearchContent = ({ text }: IProps) => {
  const appDispatch = useAppDispatch();
  const navigation = useNavigate();
  const favourites = useAppSelector((state) => state.FAVOURITES);
  const {
    relevantLocations = [],
    searchMCPUrl,
    recentSearches,
    searchStatus,
    results,
  } = useAppSelector((state) => state.SEARCH);

  const handleSearchClick = React.useCallback(
    (searchResult: ISearchResult) => {
      appDispatch(REDUX_SEARCH_SAVE_RECENT_SEARCH({ searchResult }));
      appDispatch(REDUX_SEARCH_CLOSE_OVERLAY());
      navigation(searchResult.url);
    },
    [appDispatch, navigation],
  );

  const mcpUrl: IModuleConfig | undefined = useMemo(
    () => (searchMCPUrl ? { name: 'mcp', dataUrl: searchMCPUrl } : undefined),
    [searchMCPUrl],
  );

  if (searchStatus === SearchStatus.SEARCHING) {
    return (
      <div className="u-mT-responsive u-pT-responsive u-flex u-flexJustifyCenter">
        <Spinner />
      </div>
    );
  }

  if (text.length >= SEARCH_THRESHOLD && results) {
    return (
      <div className="Grid Grid--withGutters">
        <div className="Grid-cell u-lg-width2of3">
          <SearchContentResults searchResults={results} handleSearchClick={handleSearchClick} inputValue={text} />
        </div>
      </div>
    );
  }

  const { topItems, bottomItems } = favourites;
  const favItems = [...topItems, ...bottomItems];

  return (
    <>
      {mcpUrl && <ModuleLoad config={mcpUrl} modulePath={ModulePath.MAP_SLOT_DYNAMIC} />}
      <div className="Grid Grid--withGutter">
        {recentSearches.length > 0 && (
          <div className="Grid-cell u-md-width1of2">
            <SearchContentResults recentSearches={recentSearches} title="Recent searches" />
          </div>
        )}
        {favItems.length > 0 && (
          <div className="Grid-cell u-md-width1of2">
            <SearchContentResults favourites={favItems} title="My Favourites" />
          </div>
        )}
      </div>
      {relevantLocations.length > 0 && (
        <div className="u-pB-lg">
          <Glossary sections={relevantLocations} trackingOrigin='search_bar' />
        </div>
      )}
    </>
  );
};

export const SearchContent = React.memo(_SearchContent);
